import { useState, useEffect, useCallback } from 'react';
import {
  Newspaper, RefreshCw, Send, Check, Archive, Eye, Zap,
  Filter, Clock, User, ChevronRight, AlertTriangle, Globe
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';

const STATUS_COLORS = {
  draft: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  published: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  blocked: 'bg-red-500/15 text-red-400 border-red-500/30',
  archived: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30',
};

export default function V3ContentPage() {
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pulling, setPulling] = useState(false);
  const [rewriting, setRewriting] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [apiHealth, setApiHealth] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);
  const [scheduler, setScheduler] = useState(null);
  const [schedulerRunning, setSchedulerRunning] = useState(false);
  const [quota, setQuota] = useState(null);

  const loadSites = useCallback(async () => {
    try {
      const res = await api.get('/sites');
      const v3 = (res.data || []).filter(s => s.template_id === 'media_affiliate_v3');
      setSites(v3);
      if (v3.length > 0 && !selectedSite) setSelectedSite(v3[0].id);
    } catch (err) { console.error(err); }
  }, [selectedSite]);

  const loadContent = useCallback(async () => {
    if (!selectedSite) return;
    try {
      setLoading(true);
      const params = { site_id: selectedSite };
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      const res = await api.get('/v3/content/list', { params });
      setContent(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      setContent([]);
    } finally {
      setLoading(false);
    }
  }, [selectedSite, statusFilter]);

  const loadHealth = useCallback(async () => {
    try {
      const [hRes, sRes, qRes] = await Promise.all([
        api.get('/v3/content/health'),
        api.get('/v3/scheduler/status'),
        api.get('/v3/scheduler/quota'),
      ]);
      setApiHealth(hRes.data);
      setScheduler(sRes.data);
      setQuota(qRes.data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { loadSites(); loadHealth(); }, [loadSites, loadHealth]);
  useEffect(() => { loadContent(); }, [loadContent]);

  const pullAndRewrite = async () => {
    if (!selectedSite) return;
    setRewriting(true);
    toast.info('GoSkor feed cekiliyor ve rewrite ediliyor...');
    try {
      const res = await api.post('/v3/content/rewrite-batch', { site_id: selectedSite, limit: 5 }, { timeout: 90000 });
      const d = res.data;
      toast.success(`${d.ok}/${d.total} haber islendi (kaynak: ${d.source})`);
      loadContent();
    } catch (err) {
      toast.error('Rewrite hatasi: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRewriting(false);
    }
  };

  const publishAll = async () => {
    if (!selectedSite) return;
    setPublishing(true);
    try {
      const res = await api.post('/v3/content/publish-all-drafts', { site_id: selectedSite });
      toast.success(`${res.data.published} haber yayinlandi`);
      loadContent();
      loadHealth();
    } catch (err) {
      toast.error('Publish hatasi: ' + (err.response?.data?.detail || err.message));
    } finally {
      setPublishing(false);
    }
  };

  const distributeAll = async () => {
    setRewriting(true);
    toast.info('10 siteye dagitiliyor...');
    try {
      const res = await api.post('/v3/content/distribute-all', { limit: 3 }, { timeout: 90000 });
      toast.success(`${res.data.total_distributed} haber ${res.data.total_sites} siteye dagitildi`);
      loadContent();
      loadHealth();
    } catch (err) {
      toast.error('Dagitim hatasi: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRewriting(false);
    }
  };

  const publishOne = async (id) => {
    try {
      const res = await api.post(`/v3/content/${id}/publish`);
      if (res.data.success) {
        toast.success('Haber yayinlandi');
        loadContent();
      } else {
        toast.error(res.data.error || 'Yayin hatasi');
      }
    } catch (err) {
      toast.error('Hata: ' + (err.response?.data?.detail || err.message));
    }
  };

  const runScheduler = async () => {
    setSchedulerRunning(true);
    toast.info('Scheduler calistiriliyor...');
    try {
      const res = await api.post('/v3/scheduler/run-now', {}, { timeout: 120000 });
      const d = res.data;
      toast.success(`Scheduler: ${d.rewritten_count || 0} rewrite, ${d.image_generated_count || 0} gorsel, ${d.draft_count || 0} draft`);
      loadContent();
      loadHealth();
    } catch (err) {
      toast.error('Scheduler hatasi: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSchedulerRunning(false);
    }
  };

  const archiveOne = async (id) => {
    try {
      await api.post(`/v3/content/${id}/archive`);
      toast.success('Haber arsivlendi');
      loadContent();
    } catch (err) {
      toast.error('Arsiv hatasi');
    }
  };

  const draftCount = content.filter(c => c.publish_status === 'draft').length;
  const pubCount = content.filter(c => c.publish_status === 'published').length;
  const blockedCount = content.filter(c => c.publish_status === 'blocked').length;

  return (
    <div data-testid="v3-content-page" className="space-y-6 px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 data-testid="v3-content-title" className="text-2xl sm:text-3xl font-semibold tracking-tight font-['Space_Grotesk']">
            V3 Icerik Dagitimi
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            GoSkor feed cek, rewrite et, yayinla
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedSite} onValueChange={setSelectedSite}>
            <SelectTrigger data-testid="site-select" className="w-[250px] bg-secondary/30 border-border/50">
              <SelectValue placeholder="V3 Site sec..." />
            </SelectTrigger>
            <SelectContent>
              {sites.map(s => (
                <SelectItem key={s.id} value={s.id}>{s.brand_name} ({s.domain})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPI + Actions */}
      <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-6 gap-3">
        <Card className="bg-[hsl(var(--card))] border-border/70 p-4">
          <p className="text-[11px] text-muted-foreground uppercase">GoSkor API</p>
          <p className={`text-lg font-semibold mt-1 ${apiHealth?.connected ? 'text-emerald-400' : 'text-red-400'}`}>
            {apiHealth?.connected ? 'Bagli' : 'Bagli Degil'}
          </p>
        </Card>
        <Card className="bg-[hsl(var(--card))] border-border/70 p-4">
          <p className="text-[11px] text-muted-foreground uppercase">Draft</p>
          <p className="text-lg font-semibold mt-1 text-amber-400">{draftCount}</p>
        </Card>
        <Card className="bg-[hsl(var(--card))] border-border/70 p-4">
          <p className="text-[11px] text-muted-foreground uppercase">Yayinda</p>
          <p className="text-lg font-semibold mt-1 text-emerald-400">{pubCount}</p>
        </Card>
        <Card className="bg-[hsl(var(--card))] border-border/70 p-4">
          <p className="text-[11px] text-muted-foreground uppercase">Engellenmis</p>
          <p className="text-lg font-semibold mt-1 text-red-400">{blockedCount}</p>
        </Card>
        <Button
          data-testid="pull-rewrite-btn"
          onClick={pullAndRewrite}
          disabled={rewriting || !selectedSite}
          className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:opacity-90 h-full"
        >
          {rewriting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
          {rewriting ? 'Isleniyor...' : 'Cek + Rewrite'}
        </Button>
        <Button
          data-testid="publish-all-btn"
          onClick={publishAll}
          disabled={publishing || draftCount === 0}
          variant="outline"
          className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 h-full"
        >
          <Send className="w-4 h-4 mr-2" /> Tum Draftlari Yayinla ({draftCount})
        </Button>
        <Button
          data-testid="distribute-all-btn"
          onClick={distributeAll}
          disabled={rewriting}
          variant="outline"
          className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 h-full"
        >
          <Globe className="w-4 h-4 mr-2" /> {rewriting ? 'Dagitiliyor...' : '10 Siteye Dagit'}
        </Button>
      </div>

      {/* Scheduler Panel */}
      <Card className="bg-[hsl(var(--card))] border-border/70">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-[hsl(var(--primary))]" />
              <h3 className="text-sm font-semibold">Auto Publisher Scheduler</h3>
              <Badge className={`text-[10px] ${scheduler?.status === 'running' ? 'bg-amber-500/15 text-amber-400' : scheduler?.enabled ? 'bg-emerald-500/15 text-emerald-400' : 'bg-zinc-500/15 text-zinc-400'}`}>
                {scheduler?.status === 'running' ? 'Calisiyor' : scheduler?.enabled ? 'Aktif' : 'Pasif'}
              </Badge>
              <Badge variant="outline" className="text-[10px] border-border/50">{scheduler?.mode || 'draft'}</Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="text-xs h-7 border-border/50" onClick={runScheduler} disabled={schedulerRunning}>
                {schedulerRunning ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Zap className="w-3 h-3 mr-1" />}
                {schedulerRunning ? 'Calisiyor...' : 'Simdi Calistir'}
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-6 gap-2 text-xs">
            <div className="bg-secondary/30 rounded-lg px-3 py-2">
              <span className="text-muted-foreground">Son Calisma</span>
              <p className="font-mono mt-0.5">{scheduler?.last_run ? new Date(scheduler.last_run).toLocaleString('tr-TR', {hour:'2-digit',minute:'2-digit'}) : 'Henuz yok'}</p>
            </div>
            <div className="bg-secondary/30 rounded-lg px-3 py-2">
              <span className="text-muted-foreground">Toplam Run</span>
              <p className="font-mono mt-0.5">{scheduler?.total_runs || 0}</p>
            </div>
            <div className="bg-secondary/30 rounded-lg px-3 py-2">
              <span className="text-muted-foreground">Bugun Network</span>
              <p className="font-mono mt-0.5">{quota?.network_today || 0}/{quota?.network_limit || 30}</p>
            </div>
            <div className="bg-secondary/30 rounded-lg px-3 py-2">
              <span className="text-muted-foreground">Kalan Kota</span>
              <p className="font-mono mt-0.5 text-emerald-400">{quota?.network_remaining || 0}</p>
            </div>
            <div className="bg-secondary/30 rounded-lg px-3 py-2">
              <span className="text-muted-foreground">V3 Siteler</span>
              <p className="font-mono mt-0.5">{quota?.sites?.length || 0}</p>
            </div>
            <div className="bg-secondary/30 rounded-lg px-3 py-2">
              <span className="text-muted-foreground">Interval</span>
              <p className="font-mono mt-0.5">{scheduler?.config?.interval_hours || 2}h</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filter */}
      <div className="flex gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] bg-secondary/30 border-border/50">
            <SelectValue placeholder="Durum Filtresi" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tumu</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="published">Yayinda</SelectItem>
            <SelectItem value="blocked">Engellenmis</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={loadContent} className="border-border/50">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Yenile
        </Button>
      </div>

      {/* Content Table */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}
        </div>
      ) : content.length === 0 ? (
        <Card className="bg-[hsl(var(--card))] border-border/70">
          <CardContent className="py-16 text-center">
            <Newspaper className="w-12 h-12 mx-auto text-muted-foreground/40 mb-4" />
            <p className="text-muted-foreground">
              Henuz icerik yok. "Cek + Rewrite" ile baslat.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-[hsl(var(--card))] border-border/70 overflow-hidden">
          <ScrollArea className="w-full">
            <Table>
              <TableHeader>
                <TableRow className="border-border/50 hover:bg-transparent">
                  <TableHead className="text-xs text-muted-foreground">Baslik</TableHead>
                  <TableHead className="text-xs text-muted-foreground">Kategori</TableHead>
                  <TableHead className="text-xs text-muted-foreground">Durum</TableHead>
                  <TableHead className="text-xs text-muted-foreground">Ozgunluk</TableHead>
                  <TableHead className="text-xs text-muted-foreground">Editor</TableHead>
                  <TableHead className="text-xs text-muted-foreground">Tarih</TableHead>
                  <TableHead className="text-xs text-muted-foreground text-right">Islemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {content.map(item => (
                  <TableRow key={item.id} className="border-border/30 hover:bg-secondary/20">
                    <TableCell className="font-medium text-xs max-w-[300px]">
                      <button onClick={() => setPreviewItem(item)} className="text-left hover:text-[hsl(var(--primary))] transition-colors duration-150">
                        {(item.generated_title || '').slice(0, 60)}...
                      </button>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px] border-border/50">{item.category}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={`text-[10px] ${STATUS_COLORS[item.publish_status] || ''}`}>
                        {item.publish_status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className={`text-xs font-mono ${(item.uniqueness_score || 0) >= 60 ? 'text-emerald-400' : 'text-amber-400'}`}>
                        {item.uniqueness_score || 0}%
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {item.editor_persona?.name || '-'}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {(item.created_at || '').slice(0, 10)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setPreviewItem(item)}>
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                        {item.publish_status === 'draft' && (
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-400" onClick={() => publishOne(item.id)}>
                            <Send className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {item.publish_status !== 'archived' && (
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" onClick={() => archiveOne(item.id)}>
                            <Archive className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </Card>
      )}

      {/* Preview Dialog */}
      <Dialog open={!!previewItem} onOpenChange={(open) => !open && setPreviewItem(null)}>
        <DialogContent className="max-w-2xl bg-[hsl(var(--card))] border-border/70 max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Space_Grotesk'] text-lg">{previewItem?.generated_title}</DialogTitle>
          </DialogHeader>
          {previewItem && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="bg-secondary/30 rounded-lg px-3 py-2">
                  <span className="text-muted-foreground">Slug:</span>
                  <span className="ml-2 font-mono">/haberler/{previewItem.generated_slug}</span>
                </div>
                <div className="bg-secondary/30 rounded-lg px-3 py-2">
                  <span className="text-muted-foreground">Kategori:</span>
                  <span className="ml-2">{previewItem.category}</span>
                </div>
                <div className="bg-secondary/30 rounded-lg px-3 py-2">
                  <span className="text-muted-foreground">Ozgunluk:</span>
                  <span className="ml-2 font-mono">{previewItem.uniqueness_score}%</span>
                </div>
                <div className="bg-secondary/30 rounded-lg px-3 py-2">
                  <span className="text-muted-foreground">Tier:</span>
                  <span className="ml-2">{previewItem.source_tier}</span>
                </div>
              </div>
              <Separator />
              <div>
                <p className="text-muted-foreground text-xs mb-1">Meta Title:</p>
                <p className="font-medium">{previewItem.generated_meta_title}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs mb-1">Meta Description:</p>
                <p className="text-muted-foreground">{previewItem.generated_meta_description}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs mb-1">Giris:</p>
                <p>{previewItem.generated_intro}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs mb-1">Icerik:</p>
                <div className="text-muted-foreground whitespace-pre-wrap text-xs max-h-[200px] overflow-y-auto bg-secondary/20 rounded-lg p-3" dangerouslySetInnerHTML={{ __html: previewItem.generated_article || '' }} />
              </div>
              {previewItem.generated_faq?.length > 0 && (
                <div>
                  <p className="text-muted-foreground text-xs mb-1">FAQ:</p>
                  {previewItem.generated_faq.map((f, i) => (
                    <div key={i} className="bg-secondary/20 rounded-lg p-2 mb-1 text-xs">
                      <p className="font-medium">{f.question}</p>
                      <p className="text-muted-foreground">{f.answer}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {previewItem?.publish_status === 'draft' && (
              <Button size="sm" onClick={() => { publishOne(previewItem.id); setPreviewItem(null); }} className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                <Send className="w-3.5 h-3.5 mr-1" /> Yayinla
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
